import React, { useMemo, useState } from "react"
import {
  Box,
  Stack,
  Typography,
  useTheme,
  Link,
  Tooltip,
  InfoIcon,
  IconButton,
  ClickAwayListener
} from "@carrotfertility/carotene-core"
import { useIntl } from "react-intl"
import { formatMemberId } from "#/utils/formatMemberId"
import { createPharmacyCardItem, PharmacyInfo } from "./utils/pharmacyUtils"

interface MemberIdCardProps {
  firstName: string
  lastName: string
  isUsa: boolean
  memberId: string
  effectiveDate: string
  partnerFirstName?: string
  partnerLastName?: string
  isLoading: boolean
  isError: boolean
  partnerShouldAppearOnMemberCard: boolean
  pharmacyInfo: PharmacyInfo
}

type CardItemProps = {
  label: string | React.ReactNode
  value: string
  linkText?: string
  nonLinkText?: string
  url?: string
  phoneNumber?: string
}

function CardItem({ label, value, linkText, nonLinkText, url, phoneNumber }: CardItemProps) {
  return (
    <Stack>
      <Typography variant="body2" component="div">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="bold">
        {linkText && url ? (
          <>
            <Link href={url} fontWeight="bold">
              {linkText}
            </Link>
            {nonLinkText}
          </>
        ) : (
          value
        )}
      </Typography>
      {phoneNumber && (
        <Typography variant="body2" color="text.secondary">
          {phoneNumber}
        </Typography>
      )}
    </Stack>
  )
}

export function MemberIdCard({
  firstName,
  lastName,
  isUsa,
  memberId,
  effectiveDate,
  partnerFirstName,
  partnerLastName,
  isLoading,
  partnerShouldAppearOnMemberCard,
  pharmacyInfo
}: MemberIdCardProps) {
  const theme = useTheme()
  const intl = useIntl()

  /**
   * Formats a member ID into the pattern ABC-1234-5678-X
   * Assumes input is 12 characters with 3 letters followed by 8 digits and a check digit
   */

  const formattedMemberId = !isLoading ? formatMemberId(memberId) : undefined

  const formattedEffectiveDate = useMemo(
    () =>
      intl.formatDate(effectiveDate, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC"
      }),
    [effectiveDate, intl]
  )

  const name = `${firstName} ${lastName}`

  // Get partners name if available and should be on the card
  const partnerName =
    partnerShouldAppearOnMemberCard && partnerFirstName && partnerLastName
      ? `${partnerFirstName} ${partnerLastName}`
      : undefined

  const planStartDateTooltipText = intl.formatMessage({
    defaultMessage:
      "This is when your coverage begins for the year. If an annual maximum applies to your funds, it resets when the new plan year starts."
  })

  const PlanStartDateLabel = () => {
    const [open, setOpen] = useState(false)
    return (
      <Stack direction="row" alignItems="center" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}>
        <Typography variant="body2" component="span">
          {intl.formatMessage({ defaultMessage: "Plan start date" })}
        </Typography>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Tooltip onClose={() => setOpen(false)} open={open} title={planStartDateTooltipText} placement="right">
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
              sx={{
                padding: (theme) => theme.spacing(theme.tokens.spacing.none),
                "& .MuiSvgIcon-root": {
                  fontSize: "1rem"
                }
              }}
              aria-label={intl.formatMessage({
                defaultMessage: "More info about plan start date"
              })}
            >
              <InfoIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </ClickAwayListener>
      </Stack>
    )
  }

  const cardItems: CardItemProps[] = [
    {
      label: intl.formatMessage({ defaultMessage: "Member name" }),
      value: name
    },
    ...(isUsa ? [createPharmacyCardItem(pharmacyInfo, intl.formatMessage({ defaultMessage: "Pharmacy" }), intl)] : []),
    ...(partnerName
      ? [
          {
            label: intl.formatMessage({ defaultMessage: "Partner name" }),
            value: partnerName
          }
        ]
      : []),
    ...(effectiveDate
      ? [
          {
            label: <PlanStartDateLabel />,
            value: formattedEffectiveDate
          }
        ]
      : [])
  ]

  return (
    <Stack
      sx={{
        inlineSize: 472,
        blockSize: 255,
        border: `1px solid black`,
        borderRadius: theme.tokens.borderRadius.md,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 1,
        overflow: "hidden"
      }}
    >
      <CardHeader formattedMemberId={formattedMemberId} />
      {/* Info Grid */}
      <Box
        paddingTop={theme.spacing(theme.tokens.spacing.lg)}
        paddingBottom={theme.spacing(theme.tokens.spacing.lg)}
        paddingLeft={theme.spacing(theme.tokens.spacing.md)}
        paddingRight={theme.spacing(theme.tokens.spacing.md)}
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(2,auto)`,
          gridTemplateRows: `repeat(2, 1fr)`,
          gridAutoFlow: "column",
          gap: theme.tokens.spacing.md
        }}
      >
        {cardItems.map((item, index) => (
          <CardItem
            key={index}
            label={item.label}
            value={item.value}
            linkText={item.linkText}
            nonLinkText={item.nonLinkText}
            url={item.url}
            phoneNumber={item.phoneNumber}
          />
        ))}
      </Box>
      <Footer />
    </Stack>
  )
}

function CardHeader({ formattedMemberId }: { formattedMemberId?: string }) {
  const theme = useTheme()
  const intl = useIntl()

  return (
    <>
      <Stack
        direction="row"
        padding={theme.spacing(theme.tokens.spacing.md)}
        borderBottom={`1px solid ${theme.palette.divider}`}
        sx={{
          backgroundColor: theme.palette.background.default
        }}
      >
        <Box component="img" src="/images/CarrotGrey.svg" alt="" height="22px" />
        <Typography
          variant="body2"
          sx={{
            marginInlineStart: theme.spacing(theme.tokens.spacing.md)
          }}
        >
          {intl.formatMessage({ defaultMessage: "Carrot Member ID:  " })}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={"bold"}
          sx={{
            marginInlineStart: theme.spacing(theme.tokens.spacing.xxxs)
          }}
        >
          {formattedMemberId}
        </Typography>
      </Stack>
    </>
  )
}

function Footer() {
  const theme = useTheme()
  return (
    <Stack
      alignItems="flex-end"
      padding={theme.spacing(theme.tokens.spacing.sm)}
      borderTop={`1px solid ${theme.palette.divider}`}
      sx={{
        backgroundColor: theme.palette.background.default,
        flex: 1
      }}
    ></Stack>
  )
}
